/**
 * A minimal client for WHIP ingest — WebRTC-HTTP Ingestion Protocol, as Zencastr's gateway and transcoder implement it.
 *
 * One job: take a `MediaStream` the caller already has and put it on the wire. `createWhipSession({ endpoint, stream, auth })`
 * builds a session with a `state`, a `statechange` event and a `close()`; `start()` negotiates it. Every failure is a
 * {@link WhipError} with a reason worth branching on. Silent unless you hand it a logger.
 *
 * It captures nothing. Cameras, microphones, screen shares, compositing and mixing are all the caller's — which is also
 * why it stops none of them: `close()` disposes of the peer connection and the server-side resource, and leaves the tracks
 * exactly as it found them.
 *
 * What the endpoint will accept, and what this therefore enforces: exactly one audio and one video track, and a frame
 * short side inside the range its ABR ladder allows. What it will not accept is renegotiation — the answer is pruned to a
 * single video codec chosen by the transcoder, and `PATCH` on the session resource is a no-op, so there is no trickle ICE
 * and no ICE restart. A `failed` session is replaced, not repaired.
 */

export { createWhipSession, type WhipSessionOptions } from './createWhipSession';

export { WhipSession } from './WhipSession';

export { WhipError, type WhipErrorReason } from './errors';

export type { StreamKey, WhipAuth } from './auth';

export type { WhipConnectionState, WhipEventListener, WhipEventMap, WhipEventType } from './events';

export { SILENT, type WhipLogger } from './logger';


export type { WhipDeleteOutcome, WhipPostResult } from './whip-http';
